import { bindActionCreators } from "redux";
import { useDispatch, useSelector } from "react-redux";
import React, {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import { useHistory } from "react-router-dom";
import { Input, useToast } from "@chakra-ui/react";
import { fetchTemplateThunk } from "../../redux/slices/template";
import { fetchSettingThunk } from "../../redux/slices/setting";
import { fetchThemeThunk } from "../../redux/slices/theme";
import { fetchCardThunk } from "../../redux/slices/card";
import API from "../api";

import "./typedef";
import { RootState } from "../../redux/store";
import { UniversalState } from "../../types/state";
import { User } from "../../types/user";
import { Company } from "../../types/company";
import {
  PortfolioSetting,
  Setting,
  SettingType,
  UserSetting,
} from "../../types/setting";
import axios from "axios";

export function useActions(actions: any, deps: any[] = []) {
  const dispatch = useDispatch();
  return useMemo(
    () => {
      if (Array.isArray(actions)) {
        return actions.map((a) => bindActionCreators(a, dispatch));
      }
      return bindActionCreators(actions, dispatch);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps ? [dispatch, ...deps] : [dispatch]
  );
}

/**
 * @returns {RedirectRet}
 */
export const useRedirect = () => {
  const history = useHistory();

  const redirect = useCallback(
    (path) => {
      history.push(path);
    },
    [history]
  );

  const goBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const refresh = useCallback(() => {
    history.go(0);
  }, [history]);
  return { redirect, goBack, refresh };
};

type FileCallback = (file: File) => void;
interface FileInput {
  onUpload?: FileCallback;
}

export const useFile = () => {
  const [file, setFile] = useState<File | string>(null);
  const fileRef = useRef<HTMLInputElement>();

  const handleFileChange = useCallback(
    (callback: FileCallback = () => {}) =>
      ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
        setFile(files[0]);
        callback(files[0]);
      },
    []
  );

  const FileInputComponent = ({ onUpload }: FileInput) => (
    <Input
      ref={fileRef}
      type="file"
      style={{ display: "none" }}
      onChange={handleFileChange(onUpload) as any}
    />
  );
  const upload = () => fileRef.current.click();
  return {
    file,
    FileInputComponent,
    upload,
    setFile,
  };
};

export const useFile2 = () => {
  const [file, setFile] = useState<File | string>(null);
  const fileRef = useRef<HTMLInputElement>();

  const handleFileChange = useCallback(
    (callback: FileCallback = () => {}) =>
      ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
        setFile(files[0]);
        callback(files[0]);
      },
    []
  );

  const FileInputComponent = ({ onUpload }: FileInput) => (
    <Input
      ref={fileRef}
      type="file"
      style={{ display: "none" }}
      onChange={handleFileChange(onUpload) as any}
    />
  );
  const upload = () => fileRef.current.click();
  return [file, FileInputComponent, upload, setFile];
};

export const useFetchTemplate = () => useActions(fetchTemplateThunk);
export const useFetchSetting = () => useActions(fetchSettingThunk);
export const useFetchTheme = () => {
  const _theme = useSelector(({ theme }: RootState) => theme);
  const fetchTheme = useActions(fetchThemeThunk);
  return { theme: _theme, fetchTheme };
};
export const useFetch = (key: string, action: any) => () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const state = useSelector((store) => (store as any)[key]);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const action_ = useActions(action);
  return { state, action: action_ };
};
export const useFetchCard = () => {
  const _card = useSelector(({ card }: RootState) => card);
  const fetchCard = useActions(fetchCardThunk);
  return { card: _card, fetchCard };
};
export const useAddCardForUser = () => {
  const [result, setResult] = useState({
    loading: false,
    error: null,
    data: [],
  });
  const addCard = useCallback(
    async (data) => {
      try {
        setResult({ ...result, loading: true });
        const res = await API.post("user/card", data);
        setResult({
          ...result,
          data: res.data,
          loading: false,
        });
      } catch (err) {
        setResult({
          ...result,
          error: err,
          loading: false,
        });
      }
    },
    [result]
  );
  return { result, addCard };
};

export const useAddCardForCompany = () => {
  const [result, setResult] = useState({
    loading: false,
    error: null,
    data: [],
  });
  const addCard = useCallback(
    async (data) => {
      try {
        setResult({ ...result, loading: true });
        const res = await API.put("company", data);
        setResult({
          ...result,
          data: res.data,
          loading: false,
        });
      } catch (err) {
        setResult({
          ...result,
          error: err,
          loading: false,
        });
      }
    },
    [result]
  );
  return { result, addCard };
};

export const useCreateCompany = () => {
  const [result, setResult] = useState({
    loading: false,
    error: null,
    data: {},
  });
  const createCompany = useCallback(
    async (data) => {
      try {
        setResult({ ...result, loading: true });
        // console.log({ data });
        const res = await API.post("company/admin", data);
        setResult({
          ...result,
          data: res.data,
          loading: false,
        });
      } catch (err) {
        setResult({
          ...result,
          error: err,
          loading: false,
        });
        throw err;
      }
    },
    [result]
  );
  return { result, createCompany };
};

export interface UpgradeCompanyForm {
  userId: string;
}

export const useUpgradeCompany = () => {
  const toast = useToast()
  const { error: errToast } = useErrorToast()

  const [result, setResult] = useState<UniversalState<Company>>({
    loading: false,
    error: null,
    data: null,
  });

  const resetError = useCallback(() => {
    setResult({
      ...result,
      error: null
    })
  }, [result])

  const upgradeToCompany = useCallback(
    async (data: UpgradeCompanyForm, onSuccess: () => void = () => {}) => {
      try {
        setResult({ ...result, loading: true });
        const res = await API.post("user/company/upgrade/admin", data);
        setResult({
          ...result,
          data: res.data as Company,
          loading: false,
        });
        toast({
          status: "success",
          title: "Upgrade to Company successfully",
          position: 'top',
        });
        onSuccess()
      } catch (err) {
        setResult({
          ...result,
          error: err,
          loading: false,
        });
        errToast(err)
        throw err;
      }
    },
    [errToast, result, toast]
  );

  const downgradeFromCompany = useCallback(
    async (data: UpgradeCompanyForm, onSuccess: () => void = () => {}) => {
      try {
        setResult({ ...result, loading: true });
        // console.log({ data });
        const res = await API.post("user/company/downgrade/admin", data);
        setResult({
          ...result,
          data: res.data as Company,
          loading: false,
        });
        toast({
          status: "success",
          title: "Downgrade from Company successfully",
          position: 'top',
        });
        onSuccess()
      } catch (err) {
        setResult({
          ...result,
          error: err,
          loading: false,
        });
        errToast(err)
        throw err;
      }
    },
    [errToast, result, toast]
  );

  return { result, upgradeToCompany, downgradeFromCompany,  resetError };
};

export const useFetchUsers = () => {
  const [users, setUsers] = useState<UniversalState<User[]>>({
    loading: false,
    error: null,
    data: [],
  });
  const fetchUsers = useCallback(async () => {
    try {
      setUsers({ ...users, loading: true });
      const { data }: { data: User[] } = await API.get("user");
      setUsers({ ...users, data, loading: false });
    } catch (err) {
      setUsers({
        ...users,
        error: err,
        loading: false,
      });
    }
  }, [users]);
  return { users, fetchUsers };
};
export const useFetchCompanies = () => {
  const [companies, setCompanies] = useState({
    loading: false,
    error: null,
    data: [],
  });
  const fetchCompanies = useCallback(async () => {
    try {
      setCompanies({ ...companies, loading: true });
      const { data } = await API.get("company");
      setCompanies({ ...companies, data, loading: false });
    } catch (err) {
      setCompanies({
        ...companies,
        error: err,
        loading: false,
      });
    }
  }, [companies]);
  return { companies, fetchCompanies };
};

type SettingI = {
  [key in SettingType]?: Setting<UserSetting | PortfolioSetting>;
};

export const useSetting = () => {
  const setting = useSelector(({ setting: s }: RootState) => s);
  const { data = [], loading } = setting;
  const allSetting: SettingI = data.reduce(
    (acc, current) => ({
      ...acc,
      [current.type]: current,
    }),
    {}
  );
  return { setting: allSetting, loading };
};
export const useUser = () => useSelector(({ user }: RootState) => user);

export const useDidMount = (
  callback: () => void,
  cleanup: () => any = () => {}
) => {
  useEffect(() => {
    callback();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useRole = () => {
  const { data: { type: role } = {} } = useSelector(
    ({ user }: RootState) => user
  );

  const isRoleMatch = useCallback(
    /**
     * @param {string[]} roles
     */ (roles) => {
      if (roles.length === 0) {
        return true;
      }
      return roles.includes(role);
    },
    [role]
  );

  return { role, isRoleMatch };
};

export const useErrorToast = () => {
  const toast = useToast();

  const handleErr = useCallback(
    (err) => {
      if (axios.isAxiosError(err)) {
        const {
          response: {
            data: { statusCode = "", message: statusMessage = "" } = {},
          } = {},
        } = err;
        toast({
          title: "Error",
          description:
            (statusCode && statusMessage && typeof statusMessage === "string"
              ? `${statusMessage} CODE:${statusCode}`
              : err.message) || err.message,
          isClosable: true,
          status: "error",
          position: 'top',
        });
      } else {
        toast({
          title: "Error",
          description: err,
          isClosable: true,
          status: "error",
          position: 'top',
        });
      }
    },
    [toast]
  );

  return {
    error: handleErr,
  };
};
